// components/GuestSignupForm.jsx
import React, { useState, useEffect } from 'react'
import GooglePlacesAutocomplete from "react-google-places-autocomplete"
import Input from 'react-phone-number-input/input'
import { SmartButtonStub } from 'ui/smart-buttons'
import { Formatter } from 'shared'
import { User } from 'api'

const GuestSignupForm = ({ onSuccess, verificationError, signupAddress, signupPhone, disableAddress }) => {
  const [user, setUser] = useState({ first_name: '', last_name: '', email: '', phone: signupPhone || '', address: signupAddress || '', user_role_id: '', nonce: '' })
  const [settings, setSettings] = useState({})
  const [card, setCard] = useState(null)
  const [errors, setErrors] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [showResetLink, setShowResetLink] = useState(false)

  useEffect(() => {
    const applyGooglePlacesAccessibilityFix = () => {
      const input = document.querySelector("#react-google-places-autocomplete-input");
      if (input) {
        input.setAttribute("autocomplete", "street-address");
        input.setAttribute("id", "home-address");
        input.setAttribute("name", "street-address");
        input.setAttribute("aria-label", "Street Address");
        input.style.border = "2px solid #2C2C2C";
        input.style.borderRadius = "4px";
        input.style.color = "#000000";
        input.style.backgroundColor = "#FFFFFF";
        input.style.padding = "0.5rem";
      }
    };

    setTimeout(applyGooglePlacesAccessibilityFix, 100);
  }, []);

  useEffect(() => {
    loadData()
    if (User.signedIn && User.regular) {
      App.signOut({ redirectPath: '/app' })
    }
  }, [])

  useEffect(() => {
    const setAddressInputValue = () => {
      const addressInput = document.querySelector(".google-places-autocomplete__input");
      if (addressInput) {
        addressInput.value = signupAddress || '';
      }
    };

    const intervalId = setInterval(() => {
      if (document.querySelector(".google-places-autocomplete__input")) {
        setAddressInputValue();
        clearInterval(intervalId);
      }
    }, 100);

    return () => clearInterval(intervalId);
  }, [signupAddress]);

  const loadData = async () => {
    const res = await Backend.get('/signup_settings')
    setSettings(res)
    if (res.require_payment) {
      const squarePayment = window.Square.payments(application.squareApplicationId, null)
      const cardObj = await squarePayment.card()
      await cardObj.attach('#card-container')
      setCard(cardObj)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setUser(prev => ({ ...prev, [name]: value }))
    setErrors(null)
  }

  const handlePhoneChange = (val) => {
    setUser(prev => ({ ...prev, phone: val }))
    setErrors(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const addressVal = disableAddress ? signupAddress : document.querySelector(".google-places-autocomplete__input")?.value
    const updatedUser = { ...user, address: addressVal }

    setSubmitting(true)
    if (settings.require_payment && card) {
      const result = await card.tokenize()
      if (result.status === 'OK') {
        updatedUser.nonce = result.token
      }
    }

    try {
      await Backend.post('/users', { user: updatedUser })
      setErrors(null)
      setSuccess(true)
      onSuccess?.()
    } catch (xhr) {
      if (xhr.status >= 400 && xhr.status <= 499) {
        const errors = xhr.responseJSON.errors
        console.log(errors)
        const allErrors = Object.values(errors).flat()
        if (errors?.includes('Email has already been taken')) {
          setErrors(["Email has already been taken. If you want to reset your password, click the link below.", ...allErrors.filter(msg => !msg.toLowerCase().includes('has already been taken'))])
          setShowResetLink(true)
        } else {
          setErrors(errors)
        }
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (success) {
    return (
      <p className="alert alert-success text-center">
        {App.isBigSky
          ? "Thank you for registering! Please check your email for additional welcome information and credentials."
          : "Thank you for registering! Please Sign In to activate your account."}
      </p>
    )
  }

  return (
    <form className="signup--form custom-text-community" onSubmit={handleSubmit}>
      {settings.welcome_text && (
        <p dangerouslySetInnerHTML={{ __html: Formatter.simpleFormat(settings.welcome_text) }}></p>
      )}
      {verificationError ? (
        <p className="alert alert-danger mt-2 text-center" style={{ fontSize: "16px" }}>
          {verificationError}
        </p>
      ) : (
        <>
          {errors && errors.map((err, i) => (
            <p key={i} className="alert alert-danger alert-mini text-center"  style={{ fontSize: "16px" }}>
              {err}
            </p>
          ))}
          {showResetLink && (
            <p className="alert alert-info mt-2 text-center" style={{ fontSize: '17px' }}>
              <a href="/u/password/new" target="_blank" rel="noopener noreferrer">
                Click here to reset your password
              </a>
            </p>
          )}
        </>
      )}

      <div className="row">
        <div className="col-sm-6">
          <div className="form-group">
            <label>First Name</label>
            <input
              type="text"
              name="first_name"
              value={user.first_name}
              className="form-control"
              onChange={handleChange}
              aria-label="First Name"
              autoComplete="given-name"
              style={{ border: "2px solid #2C2C2C", borderRadius: "4px", color: "#000", backgroundColor: "#FFF", padding: "0.5rem" }}
            />
          </div>
        </div>
        <div className="col-sm-6">
          <div className="form-group">
            <label>Last Name</label>
            <input
              type="text"
              name="last_name"
              value={user.last_name}
              className="form-control"
              onChange={handleChange}
              aria-label="Last Name"
              autoComplete="family-name"
              style={{ border: "2px solid #2C2C2C", borderRadius: "4px", color: "#000", backgroundColor: "#FFF", padding: "0.5rem" }}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-6">
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={user.email}
              className="form-control"
              onChange={handleChange}
              aria-label="Email Address"
              autoComplete="email"
              style={{ border: "2px solid #2C2C2C", borderRadius: "4px", color: "#000", backgroundColor: "#FFF", padding: "0.5rem" }}
            />
          </div>
        </div>
        <div className="col-sm-6">
          <div className="form-group">
            <label>Phone</label>
            <Input
              placeholder="Enter phone number"
              type="tel"
              country="US"
              international
              withCountryCallingCode
              maxLength={15}
              className="form-control"
              value={user.phone}
              onChange={handlePhoneChange}
              aria-label="Phone Number"
              autoComplete="tel"
              style={{ border: "2px solid #2C2C2C", borderRadius: "4px", color: "#000", backgroundColor: "#FFF", padding: "0.5rem" }}
            />
          </div>
        </div>
      </div>

      {!settings.hide_address && (
        <div className="form-group">
          <label>{App.isBigSky ? "Zip Code" : "Street Address"}</label>
          {
            disableAddress ?
              <input
                type="text"
                name="Address"
                className="form-control"
                value={signupAddress}
                disabled
                aria-label="Street Address"
                autoComplete="street-address"
                style={{ border: "2px solid #2C2C2C", borderRadius: "4px", color: "#000", backgroundColor: "#FFF", padding: "0.5rem" }}
              ></input> :
              <GooglePlacesAutocomplete
                apiKey={App.googleApiKey}
                placeholder="Please Enter Your Street Address"
                onSelect={({ description }) =>
                  setUser(prev => ({ ...prev, address: description }))
                }
              />
          }
        </div>
      )}

      <div className="form-group">
        <label>Select Account Type</label>
        <select
          name="user_role_id"
          className="form-control"
          value={user.user_role_id}
          onChange={handleChange}
          aria-label="Account Type"
          style={{ border: "2px solid #2C2C2C", borderRadius: "4px", color: "#000", backgroundColor: "#FFF", padding: "0.5rem" }}
        >
          <option value="">Select any</option>
          {settings.user_roles?.map(role => (
            <option key={role.id} value={role.id}>{role.name}</option>
          ))}
        </select>
      </div>

      {settings.require_payment && (
        <div className="form-group">
          <label>Payment Details</label>
          <div id="card-container"></div>
        </div>
      )}

      <div className="form-actions text-center">
        <SmartButtonStub
          text="Submit My Registration"
          buttonType="submit"
          className="primary custom-font-size custom-font-family mb-1 mb-md-0"
          spinning={submitting}
        />
      </div>
    </form>
  )
}

export default GuestSignupForm
